import { useEffect } from 'react';

import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { GQLResponsePerimeterXChallengeError } from '@change-corgi/core/gql';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { AttachedPost } from '../attachedPost';
import { PerimeterXRechallengeError, useWithPerimeterXCaptcha } from '../useWithPerimeterXCaptcha';

import { socialPostPreview } from './api/api';
import type { SocialPostPreviewQuery } from './api/api.graphql';

const QUERY_KEY = 'homepage-social-post-preview';

function getErrorReason(error: unknown): string {
	if (error instanceof PerimeterXRechallengeError) return 'CAPTCHA_RECHALLENGED';
	if (error instanceof GQLResponsePerimeterXChallengeError) return 'CAPTCHA_UNSOLVED';
	return 'OTHER';
}

/**
 * Fetches display metadata (title + thumbnail) for an attached social post, for showing a preview
 * before the user submits for draft generation. Disabled (no request) while no post is attached.
 * Tracks each request's outcome, with the server's reason, CAPTCHA_UNSOLVED for an unsolved
 * PerimeterX challenge, CAPTCHA_RECHALLENGED when the retry after a solved captcha is challenged
 * again, or OTHER for any other failure.
 */
export function useSocialPostPreview(
	post: AttachedPost | undefined,
): UseQueryResult<SocialPostPreviewQuery['socialPostPreview']> {
	const utilityContext = useUtilityContext();
	const track = useTracking();
	const withPerimeterXCaptcha = useWithPerimeterXCaptcha();
	const queryClient = useQueryClient();

	// An error result can be transient (e.g. rate limiting), so only a successful preview is kept
	// once the post is detached. Anything else, including a request still in flight that could
	// settle into an error, is dropped so a re-pasted link refetches instead of showing it.
	useEffect(() => {
		if (!post) return undefined;
		return () => {
			const queryKey = [QUERY_KEY, post];
			const data = queryClient.getQueryData<SocialPostPreviewQuery['socialPostPreview']>(queryKey);
			if (data?.__typename !== 'SocialPostPreviewSuccess') queryClient.removeQueries({ queryKey, exact: true });
		};
	}, [post, queryClient]);

	return useQuery({
		queryKey: [QUERY_KEY, post],
		queryFn: async () => {
			const { url, platform } = post as AttachedPost;
			try {
				const preview = await withPerimeterXCaptcha(async () => socialPostPreview(url, utilityContext), {
					source: url,
				});
				if (preview.__typename === 'SocialPostPreviewSuccess') {
					void track('homepage_social_post_preview_loaded', { source: url, platform });
				} else {
					void track('homepage_social_post_preview_error', { source: url, platform, reason: preview.reason });
				}
				return preview;
			} catch (error) {
				void track('homepage_social_post_preview_error', { source: url, platform, reason: getErrorReason(error) });
				throw error;
			}
		},
		enabled: post !== undefined,
	});
}
